<script lang="ts">
  import type { Redirect } from '@sveltejs/kit';
  import type { PageData } from './$types';
  import type { ButterflyAPI, ButterflyPost, ButterflyData } from '$lib/types';

  import { description } from '$lib/config';

  import MetaBase from '$components/Meta/Base.svelte';
  import MetaArticle from '$components/Meta/Article.svelte';
  import MetaJsonLd from '$components/Meta/JsonLd.svelte';
  import Breadcrumb from '$components/Breadcrumb.svelte';
  import PostHeader from '$components/Post/Header.svelte';
  import PostBody from '$components/Post/Body.svelte';
  import PostFooter from '$components/Post/Footer.svelte';
  import PostHeaderLoader from '$components/Loaders/PostHeader.svelte';
  import PostBodyLoader from '$components/Loaders/PostBody.svelte';
  import PostFooterLoader from '$components/Loaders/PostFooter.svelte';
  import Card from '$components/Card.svelte';
  import CardLoader from '$components/Loaders/Card.svelte';

  export let data: PageData;
  export let withThumb = false;
  export let withAuthorship = true;

  let scope: typeof data.scope;
  let title: string;
  let canonical: string;
  let post: ButterflyPost;
  let included: ButterflyData[];
  let resume: string;

  const setupMeta = (m: { title: string; canonical: string }) => {
    title = m.title;
    canonical = m.canonical;
  };

  const setupPost = (p: ButterflyAPI<ButterflyPost>) => {
    post = p.data;
    included = p.included;

    resume = post.attributes.resume?.length ? post.attributes.resume : description;
  };

  $: {
    scope = data.scope;
    setupMeta(scope.meta as Awaited<typeof scope.meta>);
    setupPost(scope.data as Awaited<typeof scope.data>);
  }
</script>

<svelte:head>
  <MetaBase
    robots="{scope.meta.robots}"
    title="{title}"
    description="{resume}"
    canonical="{canonical}"
    og="{{
      title: title,
      description: resume,
      type: 'article',
    }}"
  />

  <MetaArticle post="{post}" included="{included}" />
  <MetaJsonLd data="{data}" types="{['Article', 'BreadcrumbList']}" />
</svelte:head>

<div class="d-flex fd-column g4 ws12 wmx100 mx-auto p2 md:p4 md:my4 bg-light">
  <Breadcrumb data="{data}" />

  <section class="d-flex fd-column{$$slots.aside ? ' md:d-grid md:grid__3' : ''} g4">
    <article class="post{$$slots.aside ? ' grid--col2' : ''} d-flex fd-column g4">
      <PostHeader post="{scope.data}" withThumb="{withThumb}" withAuthorship="{withAuthorship}" />
      <PostBody post="{scope.data}">
        <slot name="calculator" slot="calculator" />
        <slot name="call2play" slot="call2play" />
        <slot name="results" slot="results" />
        <slot name="winnings" slot="winnings" />
      </PostBody>
      <PostFooter post="{scope.data}" />
    </article>
    <slot name="aside" />
  </section>

  <slot name="sections" />
  {#if $$slots.similarstitle}
    <section class="pt4">
      <header>
        <slot name="similarstitle" />
      </header>

      <div class="d-grid grid__2 sm:grid__4 g4">
        {#await scope.feeds.similars}
          {#each { length: 4 } as _}
            <CardLoader />
          {/each}
        {:then posts}
          {#if posts?.data?.length}
            {#each posts.data as data}
              <Card data="{data}" included="{posts.included}" resume="{false}" />
            {/each}
          {/if}
        {/await}
      </div>
    </section>
  {/if}
</div>
